import { ChangeEvent, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { getBranchValues, getRegionValues, getSourceValues, getZoneValues, getReferralDateMaxLookbackDays } from "../../configs/logic/Slice";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import FormElement from "../../../components/ui/formelement/FromElement";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { Input } from "../../../components/ui/input/SimpleInput";
import { useAbility } from "../../auth/logic/rbac";
import { getForwardingAuthorities } from "../../users/logic/slice";
import { fetchForwardingAuthorities } from "../../users/logic/thunk";
import { formatDateForInput, getCurrentDate, sortOptions } from "../../../lib/utils";
import { BasicDetailsState, validateField } from "../utils/helper";
import StepNavigation from "./StepNavigation";

type CaseBasicDetailsStepProps = {
    value: BasicDetailsState;
    onChange: (fieldName: string, value: any) => void;
    onNext: () => void;
    onPrevious?: () => void;
};

const CaseBasicDetailsStep: React.FC<CaseBasicDetailsStepProps> = ({ value, onChange, onNext, onPrevious }) => {
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const lookbackDays = useAppSelector(getReferralDateMaxLookbackDays);
    const sources = useAppSelector(getSourceValues);
    const regions = useAppSelector(getRegionValues);
    const zones = useAppSelector(getZoneValues);
    const branches = useAppSelector(getBranchValues);
    const today = formatDateForInput(getCurrentDate());
    const minDate = lookbackDays !== null ? formatDateForInput(new Date(new Date().setDate(new Date().getDate() - lookbackDays))) : undefined;
    const sourceOptions: OptionNameValuePair<string>[] = sources.map(source => ({ name: source, value: source }));
    const regionOptions: OptionNameValuePair<string>[] = regions.map(region => ({ name: region, value: region }));
    const zoneOptions: OptionNameValuePair<string>[] = zones.map(zone => ({ name: zone, value: zone }));
    const branchOptions: OptionNameValuePair<string>[] = branches.map(branch => ({ name: branch, value: branch }));


    const dispatch = useAppDispatch();
    const forwardingAuthorities = useAppSelector(getForwardingAuthorities);

    const forwardingAuthorityOptions = sortOptions(
    forwardingAuthorities?.map((user) => ({ 
        name: user.user_name || "", 
        value: user.user_id || user.email_id || "" 
    })) ?? []
    );    

    useEffect(() => {
    if (!forwardingAuthorities.length) {
        dispatch(fetchForwardingAuthorities());
    } 
    }, [dispatch, forwardingAuthorities.length]);

    const {
        source,
        referrer_name: referrerName,
        referral_date: referralDate,
        region,
        zone,
        branch,
        forwarding_authority: forwardingAuthority,
    } = value;

    const handleFormInputChange = onChange;

    const validateBasicDetailsStep = (v: BasicDetailsState, lookbackDays: number | null): Record<string, string> => {
        const errors: Record<string, string> = {};
        (["source", "referrer_name", "referral_date", "region", "zone", "branch", "forwarding_authority"] as const).forEach((field) => {
            const error = validateField(field, (v as any)[field]);
            if (error) errors[field] = error;
        });
    
        const today = formatDateForInput(getCurrentDate());
        if (v.referral_date > today) {
            errors["referral_date"] = "Referral date cannot be a future date.";
        } else if (lookbackDays !== null) {
            const minDate = formatDateForInput(new Date(new Date().setDate(new Date().getDate() - lookbackDays)));
            if (v.referral_date < minDate) {
                errors["referral_date"] = `Please select a date within the last ${lookbackDays} day${lookbackDays === 1 ? "" : "s"}`;
            }
        }
        return errors;
    };

    const handleNext = () => {
        const errors = validateBasicDetailsStep(value, lookbackDays);
        setFormErrors(errors);
        if (Object.keys(errors).length === 0) onNext();
    };

    const { can } = useAbility();

    return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-2">
            <FormElement
                name="Source"
                className="mb-4"
                inputComponent={
                    <SelectDropdownV2
                        selectedOption={source}
                        name="Source"
                        enableSearch={false}
                        options={sourceOptions || []}
                        placeholder="Select source"
                        onSelectionChange={handleFormInputChange.bind(null, "source")}
                        containerClassName="border border-gray-300 rounded-md py-2 px-3"
                        labelClassName="font-medium"
                    />
                }
                error={formErrors["source"]}
            />
            <FormElement
                name="Referrer Name"
                className="mb-4"
                inputComponent={<Input
                    id="referrer"
                    placeholder="Enter referrer's name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    type="text"
                    value={referrerName}
                    name="referrer"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleFormInputChange("referrer_name", event.target.value);
                    }}
                />}
                error={formErrors["referrer_name"]}
            />
            <FormElement
                name="Referral Date"
                className="mb-4"
                inputComponent={
                    <Input
                        id="referral_date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 leading-[1.3]"
                        type="date"
                        value={referralDate}
                        max={today}
                        min={minDate}
                        readOnly={lookbackDays === 0}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (lookbackDays === 0) return;
                            handleFormInputChange("referral_date", event.target.value);
                        }}
                    />
                }
                error={formErrors["referral_date"]}
            />
            <FormElement
                name="Region"
                className="mb-4"
                inputComponent={<SelectDropdownV2
                    name="region"
                    options={regionOptions}
                    selectedOption={region}
                    enableSearch={false}
                    onSelectionChange={handleFormInputChange.bind(this, "region")}
                    containerClassName="border border-gray-300 rounded-md py-2 px-3"
                    labelClassName="font-medium"
                />}
                error={formErrors["region"]}
            />
            <FormElement
                name="Zone"
                className="mb-4"
                inputComponent={<SelectDropdownV2
                    name="zone"
                    options={zoneOptions}
                    selectedOption={zone}
                    enableSearch={false}
                    onSelectionChange={handleFormInputChange.bind(this, "zone")}
                    containerClassName="border border-gray-300 rounded-md py-2 px-3"
                    labelClassName="font-medium"
                />}
                error={formErrors["zone"]}
            />
            <FormElement
                name="Branch"
                className="mb-4"
                inputComponent={<SelectDropdownV2
                    name="branch"
                    options={branchOptions}
                    selectedOption={branch}
                    enableSearch={true}
                    onSelectionChange={handleFormInputChange.bind(this, "branch")}
                    containerClassName="border border-gray-300 rounded-md py-2 px-3"
                    labelClassName="font-medium"
                />}
                error={formErrors["branch"]}
            />
            <div className="col-span-1 md:col-span-2 mb-4">
                <hr className="border-t border-gray-300" />
            </div>
            
            <FormElement
                name="Forwarding Authority"
                className="mb-4"
                inputComponent={<SelectDropdownV2
                    name="Forwarding Authority"
                    options={forwardingAuthorityOptions}
                    selectedOption={forwardingAuthority}
                    enableSearch={true}
                    onSelectionChange={handleFormInputChange.bind(this, "forwarding_authority")}
                    containerClassName="border border-gray-300 rounded-md py-2 px-3"
                    labelClassName="font-medium"
                />}
                error={formErrors["forwarding_authority"]}
            />
        </div>
        <StepNavigation onNext={handleNext} onPrevious={onPrevious} />
        </>
    );
}

export default CaseBasicDetailsStep;